import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Inbox, Mail } from "lucide-react";

const STATUSES = ["new", "contacted", "active", "declined"];
const statusCls = {
  new: "border-cyan/50 bg-cyan/10 text-cyan",
  contacted: "border-gold/50 bg-gold/10 text-gold",
  active: "border-acid/50 bg-acid/10 text-acid",
  declined: "border-stroke bg-elevated text-muted-gray",
};

export default function SponsorAdmin() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = () =>
    api.get("/sponsorship/inquiries")
      .then((r) => { setItems(r.data); setLoading(false); })
      .catch(() => setLoading(false));

  useEffect(() => {
    if (user?.role === "admin") fetchItems();
    else setLoading(false);
  }, [user]);

  if (user && user.role !== "admin") return <Navigate to="/app" replace />;

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/sponsorship/inquiries/${id}`, { status });
      setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked ${status}`);
    } catch { toast.error("Update failed"); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: items.filter((i) => (i.status || "new") === s).length }), {});

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Admin</div>
        <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">SPONSOR INQUIRIES</h1>
        <div className="flex flex-wrap gap-2 mt-4">
          {STATUSES.map((s) => (
            <span key={s} className={`font-mono text-[10px] uppercase px-3 py-1.5 border ${statusCls[s]}`}>{s} · {counts[s]}</span>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-muted-gray animate-pulse">Loading...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-stroke p-12 text-center">
          <Inbox className="text-muted-gray mx-auto mb-3" size={28} />
          <p className="font-mono text-sm text-muted-gray">No sponsor inquiries yet.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="sponsor-inquiries-list">
          {items.map((it) => (
            <div key={it.id} className="bg-surface border border-stroke p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="sponsor-inquiry-row">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-2xl text-paper">{it.name}</span>
                  {it.brand && <span className="font-mono text-xs text-muted-gray">· {it.brand}</span>}
                  {it.tier && <span className="font-mono text-[10px] uppercase px-2 py-0.5 border border-acid/40 text-acid">{it.tier}</span>}
                </div>
                <a href={`mailto:${it.email}`} className="font-mono text-xs text-cyan hover:underline flex items-center gap-1 mt-1">
                  <Mail size={12} /> {it.email}
                </a>
                {it.message && <p className="font-sans text-sm text-muted-gray mt-2 leading-relaxed">{it.message}</p>}
              </div>
              <select value={it.status || "new"} onChange={(e) => setStatus(it.id, e.target.value)}
                data-testid={`inquiry-status-${it.id}`}
                className={`bg-ink border px-3 py-2 font-mono text-xs uppercase focus:outline-none ${statusCls[it.status || "new"]}`}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
